import { forwardRef, memo, useCallback, type ReactNode } from 'react';
import isString from 'lodash/isString';
import { useIntl, FormattedMessage} from 'react-intl';
// Material UI imports
import ButtonGroup, { type ButtonGroupProps } from '@mui/material/ButtonGroup';
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
// SCSS imports
import { group, button, buttonSmall } from './YesNoButtonGroup.module.scss';

type YesNoButtonGroupProps = {
  readonly small?: boolean | null;
  readonly disabled?: boolean | null;
  readonly pendingValue?: boolean | null;
  readonly value: boolean;
  readonly onChange: (value: boolean) => void;
  readonly ariaLabel?: string;
  readonly yesLabel?: string | ReactNode;
  readonly noLabel?: string | ReactNode;
  readonly sx?: ButtonGroupProps['sx'];
  readonly buttonSx?: ButtonProps['sx'];
}

const YesNoButtonGroup = forwardRef<HTMLDivElement, YesNoButtonGroupProps>(({
  small = false,
  disabled = false,
  pendingValue,
  value,
  onChange,
  ariaLabel,
  yesLabel,
  noLabel,
  sx,
  buttonSx
  // eslint-disable-next-line complexity
}, ref) => {
  const { formatMessage } = useIntl();

  const handleYes = useCallback(() => {
    onChange(true);
  }, [onChange]);

  const handleNo = useCallback(() => {
    onChange(false);
  }, [onChange]);

  return (
    <ButtonGroup
        ref={ref}
        sx={sx}
        color="primary"
        disableElevation
        disableRipple
        disabled={disabled ? true : undefined}
        aria-label={ariaLabel ? formatMessage({ id: ariaLabel, defaultMessage: ariaLabel }) : undefined}
        className={small ? undefined : group}
        size={small ? 'small' : undefined}
    >
      <Button
          fullWidth
          aria-pressed={value}
          aria-label={formatMessage({
            id: (isString(yesLabel) && yesLabel as string) || 'common.yes',
            defaultMessage: (isString(yesLabel) && yesLabel as string) || undefined
          })}
          variant={value ? 'contained' : 'outlined'}
          color={value ? 'primary' : 'secondary'}
          onClick={handleYes}
          className={small ? buttonSmall : button}
          sx={buttonSx}
      >
        {(pendingValue === true && <CircularProgress color="inherit" size="1rem"/>) || (!yesLabel || isString(yesLabel) ? (
          <FormattedMessage
              id={(yesLabel as string) || (small ? 'common.yes' : 'common.big_yes')}
              defaultMessage={yesLabel as string}
          />
        ) : yesLabel)}
      </Button>
      <Button
          fullWidth
          aria-pressed={!value}
          aria-label={formatMessage({
            id: (isString(noLabel) && noLabel as string) || 'common.no',
            defaultMessage: (isString(noLabel) && noLabel as string) || undefined
          })}
          variant={value ? 'outlined' : 'contained'}
          color={value ? 'secondary' : 'primary'}
          onClick={handleNo}
          className={small ? buttonSmall : button}
          sx={buttonSx}
      >
        {(pendingValue === false && <CircularProgress color="inherit" size="1rem"/>) || (!noLabel || isString(noLabel) ? (
          <FormattedMessage
              id={(noLabel as string) || (small ? 'common.no' : 'common.big_no')}
              defaultMessage={noLabel as string}
          />
        ) : noLabel)}
      </Button>
    </ButtonGroup>
  );
});

YesNoButtonGroup.displayName = 'YesNoButtonGroup';

export default memo(YesNoButtonGroup);
